import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { BasicsSection } from "./-components/basics-section";
import { BundleSection, bundleQuery } from "./-components/bundle-section";
import { attributesQuery, categoriesQuery, holdersQuery } from "./-components/catalog-fields";
import { CatalogSection } from "./-components/catalog-section";
import { ArchivedNotice, DeleteProductAction } from "./-components/delete-product";
import { OptionsSection } from "./-components/options-section";
import { PhotosSection } from "./-components/photos-section";
import { PricingCard } from "./-components/pricing-card";
import { ProductStatusBadge } from "./-components/product-status";
import { ReadOnlyFieldset, ReadOnlyProvider } from "./-components/read-only";
import { productQuery, useProductWrites } from "./-components/use-product";
import { VariantsSection } from "./-components/variants-section";

/**
 * The product editor (#50–#53): one page, filled in from top to bottom. Basis first; the switch
 * "Dit product heeft varianten" opens the options, whose values are chips and whose variants show up
 * at once in the compact variant table, with the rest of a variant in a side panel. Every field saves
 * itself, and every write answers the whole editor, which the page shows. Photos (#54) have a card of
 * their own under Basis; the groups, attribute values and fit (#160) one under the photos.
 * "Verwijderen" (spec, A2) deletes it, or archives it when it has history; an archived product says
 * so at the top, with "Terugzetten", and is read-only until then: every field and button on the page
 * is switched off (`read-only.tsx`), while what only reads (the variant panel, the price check, the
 * links) keeps working.
 */
export const Route = createFileRoute("/_admin/product/$productId")({
	loader: async ({ context, params }) => {
		try {
			// With the lists the "Indeling" card and the variant panel choose from.
			const [product] = await Promise.all([
				context.queryClient.ensureQueryData(productQuery(params.productId)),
				context.queryClient.ensureQueryData(categoriesQuery()),
				context.queryClient.ensureQueryData(attributesQuery()),
				context.queryClient.ensureQueryData(holdersQuery()),
			]);
			if (product.kind === "bundle") await context.queryClient.ensureQueryData(bundleQuery(params.productId));
		} catch (error) {
			if (apiErrorTag(error) === "ProductNotFound") throw notFound();
			throw error;
		}
	},
	component: ProductEditorPage,
});

function ProductEditorPage() {
	const { t } = useTranslation("product");
	const { productId } = Route.useParams();
	const { data: product } = useSuspenseQuery(productQuery(productId));
	const writes = useProductWrites(productId);
	const archived = product.archivedAt !== null;

	return (
		<ReadOnlyProvider readOnly={archived}>
			<div className="flex max-w-5xl flex-col gap-6">
				<PageHeader
					back={<BackLink to="/product">{t("title")}</BackLink>}
					title={
						<span className="flex flex-wrap items-center gap-3">
							{product.name}
							<ProductStatusBadge product={product} hideActive />
						</span>
					}
					actions={archived ? undefined : <DeleteProductAction product={product} />}
				/>
				{product.archivedAt !== null ? (
					<ArchivedNotice product={product} archivedAt={product.archivedAt} />
				) : null}
				<ReadOnlyFieldset className="flex flex-col gap-6">
					<BasicsSection product={product} writes={writes} />
					<PhotosSection product={product} writes={writes} />
					<CatalogSection product={product} writes={writes} />
					{/* A bundle has one variant: no options, its components instead. */}
					{product.kind === "bundle" ? <BundleSection product={product} /> : null}
					{product.kind === "bundle" ? null : <OptionsSection product={product} writes={writes} />}
				</ReadOnlyFieldset>
				{/* These two keep what only reads usable (the variant panel, the price check): they switch off their own fields. */}
				{product.options.length > 0 || product.variants.length > 1 ? (
					<VariantsSection product={product} writes={writes} />
				) : null}
				<PricingCard product={product} />
			</div>
		</ReadOnlyProvider>
	);
}
